import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FiPlus, FiSend, FiDownload, FiThumbsUp, FiTrendingUp, FiInbox, FiUserPlus,
  FiCheck, FiX, FiMessageSquare, FiEyeOff,
} from "react-icons/fi";
import {
  getFeedback, giveFeedback, acknowledgeFeedback, getFeedbackRequests, requestFeedback,
  respondToFeedbackRequest, declineFeedbackRequest, getFeedbackSummary, getFeedbackDirectory,
} from "../../services/feedbackApi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import useAsync from "../../hooks/useAsync";
import { FEATURES } from "../../services/navigation";
import {
  Card, PageHeader, Button, Banner, Modal, FormField, Tabs, StatTile, Loader,
  EmptyState, DataTable, SearchInput,
  BackendPending,
} from "../../components/common";
import Avatar, { PersonCell } from "../../components/common/Avatar";
import {
  FEEDBACK_TYPES, FEEDBACK_VISIBILITY, FEEDBACK_RELATIONS, FEEDBACK_REQUEST_STATUS,
} from "../../utils/constants";
import { downloadXlsx } from "../../utils/exportExcel";
import { formatDate, timeAgo } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = {
  getFeedback, giveFeedback, acknowledgeFeedback, getFeedbackRequests, requestFeedback,
  respondToFeedbackRequest, declineFeedbackRequest, getFeedbackSummary, getFeedbackDirectory,
};

const DEFAULT_QUESTIONS = [
  "What should this person keep doing?",
  "What one change would make the biggest difference?",
  "How well do they collaborate across teams?",
];

const TYPE_TONE = {
  [FEEDBACK_TYPES.APPRECIATION]: "success",
  [FEEDBACK_TYPES.IMPROVEMENT]: "warning",
  [FEEDBACK_TYPES.GENERAL]: "info",
};

function FeedbackCard({ item, direction, onAcknowledge }) {
  const person = direction === "given" ? item.toName : item.fromName;
  return (
    <li className={`feedback-card tone-${TYPE_TONE[item.type]}`}>
      <div className="feedback-card-head">
        <span className="feedback-person">
          {item.anonymous && direction === "received" ? (
            <span className="avatar feedback-anon" aria-hidden="true"><FiEyeOff size={14} /></span>
          ) : (
            <Avatar name={person || "?"} size={34} />
          )}
          <span className="person-text">
            <span className="person-name">{direction === "given" ? `To ${person}` : person}</span>
            <span className="person-meta">{item.relation} · {timeAgo(item.createdAt)}</span>
          </span>
        </span>
        <span className={`badge badge-${TYPE_TONE[item.type]}`}><span className="badge-dot" />{item.type}</span>
      </div>
      <p className="feedback-message">{item.message}</p>
      {(item.strengths || item.improvements) && (
        <div className="feedback-split">
          {item.strengths && (
            <div>
              <p className="detail-label">Strengths</p>
              <p className="detail-text">{item.strengths}</p>
            </div>
          )}
          {item.improvements && (
            <div>
              <p className="detail-label">Suggestions</p>
              <p className="detail-text">{item.improvements}</p>
            </div>
          )}
        </div>
      )}
      <div className="feedback-foot">
        <span className="muted">
          {item.visibility === "anonymous" ? "Shared anonymously" : item.visibility === "manager" ? "Visible to their manager only" : "Visible to them and their manager"}
        </span>
        {direction === "received" &&
          (item.acknowledgedAt ? (
            <span className="badge badge-neutral"><span className="badge-dot" />Acknowledged</span>
          ) : (
            <Button variant="secondary" size="sm" icon={FiCheck} onClick={() => onAcknowledge(item.id)}>Acknowledge</Button>
          ))}
      </div>
    </li>
  );
}

const EMPTY_FEEDBACK = {
  toEmployeeId: "", type: FEEDBACK_TYPES.APPRECIATION, relation: "Peer",
  message: "", strengths: "", improvements: "", visibility: "shared",
};

function GiveFeedbackModal({ open, directory, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY_FEEDBACK);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const people = directory.filter(
    (p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.departmentName.toLowerCase().includes(search.toLowerCase())
  );

  const submit = async () => {
    if (!form.toEmployeeId) { toast.error("Choose who this is for."); return; }
    if (form.message.trim().length < 15) { toast.error("Write at least a sentence, with a specific example."); return; }
    setBusy(true);
    try {
      await api.giveFeedback(form);
      toast.success("Feedback sent.");
      setForm(EMPTY_FEEDBACK);
      onDone();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      size="lg"
      title="Give feedback"
      subtitle="Specific and recent beats general and late. One example is worth a paragraph of adjectives."
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={FiSend} onClick={submit} loading={busy}>Send feedback</Button>
        </>
      }
    >
      <div className="form-stack">
        <FormField label="Who is this for?" required>
          <div className="stack">
            <SearchInput value={search} onChange={setSearch} placeholder="Find a colleague" />
            <ul className="people-picker">
              {people.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`picker-item ${form.toEmployeeId === p.id ? "is-active" : ""}`}
                    onClick={() => setForm({ ...form, toEmployeeId: p.id, relation: p.relation })}
                  >
                    <Avatar name={p.name} size={30} />
                    <span className="person-text">
                      <span className="person-name">{p.name}</span>
                      <span className="person-meta">{p.designationName}, {p.departmentName}</span>
                    </span>
                  </button>
                </li>
              ))}
              {!people.length && <li className="muted picker-empty">Nobody matches that search.</li>}
            </ul>
          </div>
        </FormField>

        <div className="form-grid">
          <FormField label="Type" required>
            <div className="segmented" role="radiogroup" aria-label="Feedback type">
              {Object.values(FEEDBACK_TYPES).map((type) => (
                <button
                  key={type} type="button" role="radio" aria-checked={form.type === type}
                  className={form.type === type ? "is-active" : ""}
                  onClick={() => setForm({ ...form, type })}
                >
                  {type}
                </button>
              ))}
            </div>
          </FormField>
          <FormField label="Your relationship" required>
            <Select value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}>
              {FEEDBACK_RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </FormField>
        </div>

        <FormField label="What happened?" required hint="Describe the situation and the effect it had.">
          <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="During the September release you..." />
        </FormField>
        <div className="form-pair">
          <FormField label="Strengths to keep">
            <textarea rows={2} value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} />
          </FormField>
          <FormField label="One thing to change">
            <textarea rows={2} value={form.improvements} onChange={(e) => setForm({ ...form, improvements: e.target.value })} />
          </FormField>
        </div>
        <FormField label="Who can see it" required>
          <Select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
            {FEEDBACK_VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </Select>
        </FormField>
      </div>
    </Modal>
  );
}

function RequestFeedbackModal({ open, directory, onClose, onDone }) {
  const [reviewerIds, setReviewerIds] = useState([]);
  const [relation, setRelation] = useState("Peer");
  const [dueDate, setDueDate] = useState("2026-09-30");
  const [note, setNote] = useState("");
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reviewerIds.length) { toast.error("Pick at least one reviewer."); return; }
    setBusy(true);
    try {
      await api.requestFeedback({ reviewerIds, relation, dueDate, note, questions: questions.filter(Boolean) });
      toast.success(`Request sent to ${reviewerIds.length} ${reviewerIds.length === 1 ? "person" : "people"}.`);
      setReviewerIds([]);
      onDone();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      size="lg"
      title="Ask for feedback"
      subtitle="Pick the people who have seen your work this cycle."
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={FiSend} onClick={submit} loading={busy}>Send {reviewerIds.length ? `${reviewerIds.length} ` : ""}request{reviewerIds.length === 1 ? "" : "s"}</Button>
        </>
      }
    >
      <div className="form-stack">
        <FormField label="Reviewers" required>
          <ul className="people-picker is-multi">
            {directory.map((p) => (
              <li key={p.id}>
                <label className={`picker-item ${reviewerIds.includes(p.id) ? "is-active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={reviewerIds.includes(p.id)}
                    onChange={() => setReviewerIds((ids) => (ids.includes(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]))}
                  />
                  <Avatar name={p.name} size={30} />
                  <span className="person-text">
                    <span className="person-name">{p.name}</span>
                    <span className="person-meta">{p.designationName}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </FormField>
        <div className="form-grid">
          <FormField label="Their relationship to you" required>
            <Select value={relation} onChange={(e) => setRelation(e.target.value)}>
              {FEEDBACK_RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </FormField>
          <FormField label="Respond by" required>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Questions" hint="Three short questions get better answers than one broad one.">
          <div className="stack">
            {questions.map((q, i) => (
              <input
                key={i}
                className="form-control"
                value={q}
                aria-label={`Question ${i + 1}`}
                onChange={(e) => setQuestions((list) => list.map((x, idx) => (idx === i ? e.target.value : x)))}
              />
            ))}
            <Button variant="ghost" size="sm" icon={FiPlus} onClick={() => setQuestions((q) => [...q, ""])}>Add a question</Button>
          </div>
        </FormField>
        <FormField label="A note with the request">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why you are asking and how it will be used" />
        </FormField>
      </div>
    </Modal>
  );
}

function RespondModal({ request, onClose, onDone }) {
  const [answers, setAnswers] = useState({});
  const [strengths, setStrengths] = useState("");
  const [improvements, setImprovements] = useState("");
  const [visibility, setVisibility] = useState("shared");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const filled = (request.questions || []).filter((q) => (answers[q.id] || "").trim());
    if (!filled.length) { toast.error("Answer at least one question."); return; }
    setBusy(true);
    try {
      await api.respondToFeedbackRequest(request.id, {
        answers: (request.questions || []).map((q) => ({ questionId: q.id, answer: answers[q.id] || "" })),
        strengths, improvements, visibility,
      });
      toast.success("Feedback submitted.");
      onDone();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(request)}
      size="lg"
      title={`Feedback for ${request?.requesterName}`}
      subtitle={request ? `${request.relation} · due ${formatDate(request.dueDate)}` : ""}
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={FiSend} onClick={submit} loading={busy}>Submit feedback</Button>
        </>
      }
    >
      {request && (
        <div className="form-stack">
          {request.note && <Banner tone="info" title={`${request.requesterName} wrote`}>{request.note}</Banner>}
          {(request.questions || []).map((q) => (
            <FormField key={q.id} label={q.text}>
              <textarea rows={3} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
            </FormField>
          ))}
          <div className="form-pair">
            <FormField label="Strengths to keep">
              <textarea rows={2} value={strengths} onChange={(e) => setStrengths(e.target.value)} />
            </FormField>
            <FormField label="One thing to change">
              <textarea rows={2} value={improvements} onChange={(e) => setImprovements(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Who can see it" required>
            <Select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              {FEEDBACK_VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </Select>
          </FormField>
        </div>
      )}
    </Modal>
  );
}

function FeedbackPageScreen() {
  const { cycle, cycleId } = useCycle();
  const { profile } = useRole();
  const [tab, setTab] = useState("received");
  const [giveOpen, setGiveOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [responding, setResponding] = useState(null);

  const received = useAsync(() => api.getFeedback({ direction: "received", cycleId }), [cycleId]);
  const given = useAsync(() => api.getFeedback({ direction: "given", cycleId }), [cycleId]);
  const incoming = useAsync(() => api.getFeedbackRequests({ direction: "incoming", cycleId }), [cycleId]);
  const outgoing = useAsync(() => api.getFeedbackRequests({ direction: "outgoing", cycleId }), [cycleId]);
  const summary = useAsync(() => api.getFeedbackSummary({ cycleId }), [cycleId]);
  const directory = useAsync(() => api.getFeedbackDirectory(), []);

  const stats = summary.data || {};
  const pendingIncoming = (incoming.data || []).filter((r) => r.status === FEEDBACK_REQUEST_STATUS.PENDING);

  const reloadAll = () => {
    received.reload(); given.reload(); incoming.reload(); outgoing.reload(); summary.reload();
  };

  const acknowledge = async (id) => {
    try {
      await api.acknowledgeFeedback(id);
      toast.success("Acknowledged.");
      received.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const decline = async (request) => {
    try {
      await api.declineFeedbackRequest(request.id, { reason: "" });
      toast.success("Request declined.");
      incoming.reload();
      summary.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const exportFeedback = () =>
    downloadXlsx(`feedback-${cycle?.name || ""}`, [
      {
        name: "Received",
        title: `Feedback received — ${profile?.name || ""}`,
        columns: [
          { key: "createdAt", label: "Date", value: (r) => formatDate(r.createdAt) },
          { key: "fromName", label: "From" },
          { key: "relation", label: "Relationship" },
          { key: "type", label: "Type" },
          { key: "message", label: "Feedback" },
          { key: "strengths", label: "Strengths" },
          { key: "improvements", label: "Suggestions" },
        ],
        rows: received.data || [],
      },
      {
        name: "Given",
        columns: [
          { key: "createdAt", label: "Date", value: (r) => formatDate(r.createdAt) },
          { key: "toName", label: "To" },
          { key: "relation", label: "Relationship" },
          { key: "type", label: "Type" },
          { key: "message", label: "Feedback" },
        ],
        rows: given.data || [],
      },
      {
        name: "Requests",
        columns: [
          { key: "reviewerName", label: "Reviewer" }, { key: "relation", label: "Relationship" },
          { key: "status", label: "Status" }, { key: "dueDate", label: "Due", value: (r) => formatDate(r.dueDate) },
        ],
        rows: outgoing.data || [],
      },
    ]);

  const list = useMemo(() => (tab === "received" ? received.data : given.data) || [], [tab, received.data, given.data]);

  return (
    <div className="page">
      <PageHeader
        title="Feedback"
        subtitle={`Continuous and 360 feedback for ${cycle?.name}. It feeds the review conversation, not the score directly.`}
        actions={
          <>
            <Button variant="ghost" icon={FiDownload} onClick={exportFeedback}>Excel</Button>
            <Button variant="secondary" icon={FiUserPlus} onClick={() => setRequestOpen(true)}>Ask for feedback</Button>
            <Button icon={FiPlus} onClick={() => setGiveOpen(true)}>Give feedback</Button>
          </>
        }
      />

      {pendingIncoming.length > 0 && (
        <Banner
          tone="warning"
          title={`${pendingIncoming.length} ${pendingIncoming.length === 1 ? "person is" : "people are"} waiting on your feedback`}
          action={<Button size="sm" onClick={() => setTab("requests")}>See requests</Button>}
        >
          The earliest is due {formatDate(pendingIncoming[0].dueDate)}.
        </Banner>
      )}

      <div className="stat-grid">
        <StatTile label="Received" value={stats.received ?? "—"} icon={FiInbox} hint="This cycle" />
        <StatTile label="Appreciation" value={stats.appreciation ?? "—"} icon={FiThumbsUp} tone="success" hint="Things to keep doing" />
        <StatTile label="Suggestions" value={stats.improvement ?? "—"} icon={FiTrendingUp} tone="warning" hint="Things to work on" />
        <StatTile label="Given by you" value={stats.given ?? "—"} icon={FiMessageSquare} tone="info" hint="Feedback you wrote" />
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "received", label: "Received", count: (received.data || []).length },
          { key: "given", label: "Given", count: (given.data || []).length },
          { key: "requests", label: "Requests for you", count: pendingIncoming.length },
          { key: "sent", label: "Your requests", count: (outgoing.data || []).length },
        ]}
      />

      {(tab === "received" || tab === "given") && (
        <Card padded={false} title={tab === "received" ? "Feedback about you" : "Feedback you have given"}>
          {(tab === "received" ? received.loading : given.loading) && !list.length ? (
            <Loader />
          ) : list.length === 0 ? (
            <EmptyState
              title={tab === "received" ? "No feedback yet" : "You have not given feedback yet"}
              text={tab === "received" ? "Ask a few colleagues for input before your review." : "A specific note about recent work is the most useful thing you can send someone."}
              action={<Button icon={tab === "received" ? FiUserPlus : FiPlus} onClick={() => (tab === "received" ? setRequestOpen(true) : setGiveOpen(true))}>
                {tab === "received" ? "Ask for feedback" : "Give feedback"}
              </Button>}
            />
          ) : (
            <ul className="feedback-list">
              {list.map((item) => (
                <FeedbackCard key={item.id} item={item} direction={tab} onAcknowledge={acknowledge} />
              ))}
            </ul>
          )}
        </Card>
      )}

      {tab === "requests" && (
        <Card padded={false} title="People asking you for feedback">
          {incoming.loading && !incoming.data ? <Loader /> : (
            <DataTable
              rows={incoming.data || []}
              emptyTitle="No requests"
              emptyText="When a colleague asks for your input it will show up here."
              columns={[
                { key: "requesterName", label: "From", render: (r) => <PersonCell name={r.requesterName} meta={r.relation} /> },
                { key: "questions", label: "Questions", render: (r) => <span className="muted">{(r.questions || []).length} questions</span> },
                { key: "dueDate", label: "Due", render: (r) => formatDate(r.dueDate) },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => {
                    const tone = { Pending: "warning", Submitted: "success", Declined: "neutral" }[r.status];
                    return <span className={`badge badge-${tone}`}><span className="badge-dot" />{r.status}</span>;
                  },
                },
                {
                  key: "act",
                  label: "",
                  align: "right",
                  render: (r) =>
                    r.status === FEEDBACK_REQUEST_STATUS.PENDING && (
                      <span className="row-actions">
                        <Button variant="ghost" size="sm" icon={FiX} onClick={() => decline(r)}>Decline</Button>
                        <Button variant="secondary" size="sm" onClick={() => setResponding(r)}>Respond</Button>
                      </span>
                    ),
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "sent" && (
        <Card padded={false} title="Requests you have sent">
          {outgoing.loading && !outgoing.data ? <Loader /> : (
            <DataTable
              rows={outgoing.data || []}
              emptyTitle="No requests sent"
              emptyText="Pick three or four people who have seen your work this year."
              columns={[
                { key: "reviewerName", label: "Reviewer", render: (r) => <PersonCell name={r.reviewerName} meta={r.relation} /> },
                { key: "dueDate", label: "Due", render: (r) => formatDate(r.dueDate) },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => {
                    const tone = { Pending: "warning", Submitted: "success", Declined: "neutral" }[r.status];
                    return <span className={`badge badge-${tone}`}><span className="badge-dot" />{r.status}</span>;
                  },
                },
                { key: "createdAt", label: "Sent", render: (r) => timeAgo(r.createdAt) },
              ]}
            />
          )}
        </Card>
      )}

      <GiveFeedbackModal open={giveOpen} directory={directory.data || []} onClose={() => setGiveOpen(false)} onDone={reloadAll} />
      <RequestFeedbackModal open={requestOpen} directory={directory.data || []} onClose={() => setRequestOpen(false)} onDone={reloadAll} />
      {responding && <RespondModal request={responding} onClose={() => setResponding(null)} onDone={reloadAll} />}
    </div>
  );
}

const NEEDED_ENDPOINTS = [
  ["GET / POST", "/feedback", "Given / received feedback, give feedback"],
  ["POST", "/feedback/{feedbackId}/acknowledge", "Acknowledge feedback"],
  ["GET / POST", "/feedback/requests", "360 requests (incoming / outgoing), request feedback"],
  ["POST", "/feedback/requests/{requestId}/respond | decline", "Answer or decline a request"],
  ["GET", "/feedback/summary", "Counts for the summary tiles"],
  ["GET", "/employees/feedback-directory", "People the user may give feedback to"],
];

export default function FeedbackPage() {
  if (!FEATURES.feedback) {
    return <BackendPending title="Feedback" purpose="Continuous and 360-degree feedback." endpoints={NEEDED_ENDPOINTS} flag="REACT_APP_FEATURE_FEEDBACK" />;
  }
  return <FeedbackPageScreen />;
}
